import React from 'react';
import ScopedCssBaseline from '@material-ui/core/ScopedCssBaseline';
import {
  createMuiTheme,
  ThemeProvider,
  styled,
  Typography as MuiTypography,
} from '@material-ui/core';

const StyledScopedCssBaseline = styled(ScopedCssBaseline)({
  fontFamily:
    "'Suisse-Intl','Source_Sans_Pro-Regular','Roboto-Regular',Arial,Verdana,Sans Serif",
  color: '#333',
  paddingTop: 20,
  backgroundColor: 'white',
});

export const theme = createMuiTheme({
  typography: {
    subtitle: {
      ['@media screen and (min-width: 992px)']: {
        fontSize: 26,
        letterSpacing: '0.49px',
        lineHeight: '32px',
      },
      fontSize: 12,
      letterSpacing: '-0.31px',
      lineHeight: '13px',
      textTransform: 'uppercase',
      textAlign: 'center',
    },
    title: {
      ['@media screen and (min-width: 992px)']: {
        fontSize: '96px',
        letterSpacing: '1.39px',
        lineHeight: '105px',
        marginTop: '20px',
        marginBottom: '10px',
      },
      fontSize: '32px',
      fontWeight: 200,
      letterSpacing: '0.46px',
      lineHeight: '35px',
      marginTop: '15px',
      marginBottom: '15px',
      textAlign: 'center',
    },
    overview: {
      ['@media screen and (min-width: 992px)']: {
        fontSize: '32px',
        fontWeight: 300,
        letterSpacing: '0.61px',
        lineHeight: '40px',
      },
      fontSize: '20px',
      fontWeight: 300,
      letterSpacing: '0.38px',
      lineHeight: '24px',
    },
    textblock: {
      ['@media screen and (min-width: 992px)']: {
        fontSize: '20px',
        lineHeight: '32px',
      },
      fontSize: '16px',
      letterSpacing: 0,
      lineHeight: '24px',
    },
    quote: {
      ['@media screen and (min-width: 992px)']: {
        fontSize: '32px',
        lineHeight: '40px',
        letterSpacing: '0.61px',
      },
      fontSize: '20px',
      lineHeight: '24px',
      letterSpacing: '0.38px',
      fontWeight: 200,
      paddingTop: '20px',
      paddingBottom: '20px',
      whiteSpace: 'pre-line',
    },
    button: {
      ['@media screen and (min-width: 992px)']: {
        fontSize: '20px',
        lineHeight: '25px',
        letterSpacing: '0.38px',
      },
      fontSize: '16px',
      lineHeight: '20px',
      letterSpacing: 0,
      textTransform: 'none',
      fontWeight: 400,
      fontFamily:
        "'Suisse-Intl','Source_Sans_Pro-Regular','Roboto-Regular',Arial,Verdana,Sans Serif",
    },
    showroomTitle: {
      ['@media screen and (min-width: 992px)']: {
        marginBottom: 40,
        fontSize: '60px',
        lineHeight: '66px',
        letterSpacing: '1.14px',
      },
      marginBottom: 20,
      marginTop: 20,
      fontWeight: 500,
      fontSize: 30,
      fontFamily: 'inherit',
      lineHeight: '1.1',
      textAlign: 'center',
    },
    showroomSubtitle: {
      ['@media screen and (min-width: 992px)']: {
        fontSize: '26px',
        lineHeight: '32px',
      },
      letterSpacing: '0.49px',
      fontSize: '16px',
      textAlign: 'center',
    },
    designerSubtitle: {
      // color: '#EC0E00',
      color: '#e30613',
      '@media screen and (min-width: 992px)': {
        fontSize: '32px',
        lineHeight: '40px',
        letterSpacing: '0.61px',
      },
      fontWeight: 200,
      fontSize: '20px',
      lineHeight: '24px',
      letterSpacing: '0.38px',
    },
    designerTitle: {
      '@media screen and (min-width: 992px)': {
        fontSize: '40px',
        lineHeight: '40px',
      },
      fontWeight: 200,
      fontSize: '24px',
      lineHeight: '24px',
      letterSpacing: '0.46px',
    },
    categoryPreview: {
      '@media screen and (min-width: 992px)': {
        fontSize: '24px',
        lineHeight: '40px',
      },
      letterSpacing: '0.49px',
      fontSize: '16px',
      textAlign: 'center',
      fontFamily:
        '"FSSiena-Light","Source_Sans_Pro-Light","Roboto-Light",Arial,Verdana,Sans Serif',
    },
  },
  palette: {
    primary: {
      main: '#e30613',
      // main: '#EC0E00',
    },
    secondary: {
      main: '#454f5b',
    },
    background: {
      grey: '#f3f3f3',
    },
  },
  overrides: {
    MuiButton: {
      root: {
        height: 30,
      },
      textPrimary: {
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiTab: {
      root: {
        '@media screen and (min-width: 992px)': {
          fontSize: '32px',
          letterSpacing: '0.61px',
          lineHeight: '40px',
          padding: '0px 25px 15px 25px',
        },
        fontSize: '18px',
        fontWeight: 300,
        letterSpacing: 'normal',
        lineHeight: '20px',
        whiteSpace: 'nowrap',
        maxWidth: 'none',
        padding: '0px 10px 10px 10px',
        borderBottom: '2px solid #D8D8D8',
      },
    },
  },
  props: {
    MuiTypography: {
      variantMapping: {
        h1: 'h1',
        h2: 'h2',
        h3: 'h3',
        h4: 'h4',
        h5: 'h5',
        h6: 'h6',
        subtitle1: 'h6',
        subtitle2: 'h6',
        body1: 'p',
        body2: 'p',
        subtitle: 'p',
        title: 'p',
        overview: 'p',
        textblock: 'p',
        quote: 'p',
        showroomTitle: 'p',
        showroomSubtitle: 'p',
        designerSubtitle: 'p',
        designerTitle: 'p',
      },
    },
    MuiButton: {
      disableRipple: true,
      disableFocusRipple: true,
    },
    MuiAccordion: {
      elevation: 0,
    },
  },
});

export const Typography = styled(MuiTypography)(({ theme, variant }) => ({
  ...theme.typography[variant],
}));

export const PageGeneratorThemeProvider = ({ children }) => {
  return (
    <ThemeProvider theme={theme}>
      <StyledScopedCssBaseline>{children}</StyledScopedCssBaseline>
    </ThemeProvider>
  );
};
